import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiEye, FiSearch } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Input from "@/components/shared/ui/Input";
import { useEmailTemplates } from "@/api/features/email-templates";

export default function ViewEmailTemplates() {
  const [q, setQ] = useState("");
  const { data, isLoading, isError, error, refetch } = useEmailTemplates();

  const items = data?.items ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;

    return items.filter((item) =>
      [item.name, item.key, item.category, item.description, item.subject]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [items, q]);

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Email Templates</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search templates…"
            className="w-64 pl-9"
          />
        </div>
      </div>

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load email templates: {(error as Error)?.message || "Unknown error"}
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-3 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
          >
            Retry
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 text-gray-600">
              <tr className="border-b border-gray-200">
                <Th>S.No</Th>
                <Th>Name</Th>
                <Th>Category</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">
                    Loading email templates…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">
                    No email templates found.
                  </td>
                </tr>
              ) : (
                filtered.map((item, index) => (
                  <tr key={item.key} className="hover:bg-gray-50/60">
                    <Td className="text-gray-700">{index + 1}</Td>
                    <Td>
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="mt-1 text-xs text-gray-500">{item.key}</div>
                    </Td>
                    <Td className="text-gray-700">{item.category}</Td>
                    <Td className="text-gray-700">{item.subject}</Td>
                    <Td>
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        {item.status}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex items-center justify-end">
                        <Link
                          to={`/email-templates/${encodeURIComponent(item.key)}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                          aria-label="View email"
                          title="View email"
                        >
                          <FiEye />
                        </Link>
                      </div>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-gray-200 md:hidden">
          {isLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading email templates…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No email templates found.</div>
          ) : (
            filtered.map((item, index) => (
              <Link
                key={item.key}
                to={`/email-templates/${encodeURIComponent(item.key)}`}
                className="block p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {index + 1}. {item.name}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{item.subject}</div>
                  </div>
                  <FiEye className="mt-0.5 text-gray-500" />
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Th({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-5 py-3.5 ${className}`}>{children}</td>;
}
